import { FC, useEffect, useMemo, useState } from "react";
import { Pivot, PivotItem, Stack } from "@fluentui/react";
import { useTheme } from "cayo.ui";
import styled from "styled-components";
import { IAdditionalFormProps } from "@cayo/shared/components/Form/types";
import { evaluateODataLogicalExpression } from "@cayo/shared/scheme/transforms/odataLogicalExpressionEvaluator";
import { getPluralSuffix } from "@cayo/shared/utils/string";
import renderForm from "./renderers";
import { ITab } from "../../api/schema.api";

type TabProps = Omit<ITab, "type"> & {
  addFormProps?: IAdditionalFormProps;
  model: any;
};

const Tab: FC<TabProps> = ({ items, model, addFormProps }) => {
  const { cayoTheme } = useTheme();
  const { stackTokens } = cayoTheme?.metrics;

  const visibleTabs = useMemo(() => {
    const result = items?.filter((t) => {
      const expression = t.bindings?.["visible"];
      const isVisible = expression ? evaluateODataLogicalExpression(expression, model) : true;
      // TODO: OOF
      return /*true || */ isVisible;
    });

    return result || [];
  }, [items]);

  // Pivot should be controllable, do not set selectedTab to undefined
  // Initiate first selectedTab depending on visibleTabs
  const [selectedTab, setSelectedTab] = useState<string | null>(() => {
    if (visibleTabs?.length) {
      return visibleTabs[0].title ?? null;
    }
    return null;
  });

  useEffect(() => {
    const currentIndex = visibleTabs.findIndex((i) => i.title === selectedTab);

    function getNextStepIndex(forward: boolean) {
      let nextStep = currentIndex;
      nextStep += forward ? 1 : -1;
      if (nextStep >= 0 && nextStep <= visibleTabs.length - 1) {
        return nextStep;
      }

      return -1;
    }

    if (selectedTab && addFormProps?.setTabNavigation) {
      const nextStepIndex = getNextStepIndex(true);
      const prevStepIndex = getNextStepIndex(false);
      const nextStepTitle = visibleTabs[nextStepIndex]?.title ?? null;
      const prevStepTitle = visibleTabs[prevStepIndex]?.title ?? null;

      addFormProps.setTabNavigation({
        onNext:
          nextStepIndex >= 0
            ? () => {
                setSelectedTab(nextStepTitle);
                return Promise.resolve();
              }
            : undefined,
        onPrev: prevStepIndex >= 0 ? () => setSelectedTab(prevStepTitle) : undefined,
        currentIndex,
      });
    }
  }, [visibleTabs, selectedTab, addFormProps?.setTabNavigation]);

  const tabsWithInvalidFields: Map<string, string[]> = useMemo(() => {
    // the key is a tab name (title) and its value is an array of invalid fields
    const result: typeof tabsWithInvalidFields = new Map();

    for (const tab of visibleTabs) {
      const tabTitle = tab.title!;

      if (tab.items?.length) {
        const fieldsWithErrors: string[] = [];
        for (const item of tab.items) {
          const name = item.name || "";

          // check if error with that name exists
          const hasError = Boolean(addFormProps?.errors[name]);
          if (hasError) {
            // and only after that we check for visibility on the form
            const expression = item.bindings?.["visible"];
            const isVisible = expression
              ? evaluateODataLogicalExpression(expression, addFormProps?.model)
              : true;
            if (isVisible) {
              fieldsWithErrors.push(name);
            } else {
              addFormProps?.setErrorCallback(item.name!, undefined);
            }
          }
        }

        if (fieldsWithErrors.length) {
          result.set(tabTitle, fieldsWithErrors);
        }
      }
    }

    return result;
  }, [addFormProps?.errors, addFormProps?.model, visibleTabs]);

  useEffect(() => {
    // error handling by invalid fields in tabs
    const numberOfErrors = [...tabsWithInvalidFields.values()].reduce(
      (acc, val) => acc + val.length,
      0
    );
    if (numberOfErrors) {
      const formatErrorMessage = () => {
        const pluralErrors = getPluralSuffix(numberOfErrors);
        const pluralTabs = getPluralSuffix(tabsWithInvalidFields.size);
        const lastTabInMap = Array.from(tabsWithInvalidFields).at(-1)?.[0];

        let errorMessage = `Total ${numberOfErrors} error${pluralErrors} found on tab${pluralTabs}: `;

        tabsWithInvalidFields.forEach((fieldNames, tabName) => {
          const tabDelimiter = lastTabInMap === tabName ? "." : "; ";
          if (fieldNames.length) {
            errorMessage += `${tabName} (${fieldNames.join(", ")})${tabDelimiter}`;
          }
        });
        return errorMessage;
      };
      addFormProps?.setMessage?.({
        type: "error",
        message: formatErrorMessage(),
      });
    } else {
      addFormProps?.setMessage?.(undefined);
    }
  }, [tabsWithInvalidFields]);

  return (
    <Stack styles={{ root: { height: "100%", minHeight: 300 } }}>
      <Pivot
        overflowBehavior="menu"
        selectedKey={selectedTab}
        onLinkClick={(p) => {
          setSelectedTab(p?.props.itemKey ?? null);
        }}
        styles={{
          icon: { color: cayoTheme.brandColors.critical },
          root: {
            position: "sticky",
            top: 0,
            backgroundColor: cayoTheme.brandColors.primaryBackground,
            zIndex: 1,
            display: addFormProps?.isWizard ? "none" : undefined,
          },
        }}
      >
        {visibleTabs?.map((t) => {
          const tabTitle = t.title!;

          if (t.hidden) {
            return null; // Skip rendering this tab if it is marked as hidden
          }

          return (
            <PivotItem
              id={`pivot_${tabTitle}`}
              key={tabTitle}
              headerText={tabTitle}
              itemKey={tabTitle}
              alwaysRender
              // if error exists with this tab name -> show icon
              itemIcon={tabsWithInvalidFields.has(tabTitle) ? "faWarning" : undefined}
            >
              <TabContentWrapper tokens={stackTokens}>
                {renderForm(t, addFormProps!)}
              </TabContentWrapper>
            </PivotItem>
          );
        })}
      </Pivot>
    </Stack>
  );
};

const TabContentWrapper = styled(Stack)`
  position: relative;
  height: 100%;
  width: 100%;
  display: flex;
  flex-flow: column;
  overflow: hidden;
`;

export default Tab;
